import React, { useState, useEffect } from 'react';
import './LevelSettings.css';

/**
 * Level Settings Admin Component
 * Manages points configuration and level thresholds
 */
const LevelSettings = () => {
  const [pointsConfig, setPointsConfig] = useState([]);
  const [levelConfig, setLevelConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeSection, setActiveSection] = useState('points');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await fetch('/backend/api/admin/level-settings.php', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setPointsConfig(data.points_config);
        setLevelConfig(data.level_config);
      }
    } catch (error) {
      console.error('Error fetching level settings:', error);
      setMessage('Error loading configuration');
    } finally {
      setLoading(false);
    }
  };

  const updatePointsConfig = async () => {
    try {
      setSaving(true);
      setMessage('');

      const response = await fetch('/backend/api/admin/level-settings.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'points',
          data: pointsConfig
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setMessage('Points configuration updated successfully');
      } else {
        setMessage('Error updating points configuration');
      }
    } catch (error) {
      console.error('Error updating points config:', error);
      setMessage('Error updating points configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateLevelConfig = async () => {
    try {
      setSaving(true);
      setMessage('');

      const response = await fetch('/backend/api/admin/level-settings.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'levels',
          data: levelConfig
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setMessage('Level configuration updated successfully');
      } else {
        setMessage('Error updating level configuration');
      }
    } catch (error) {
      console.error('Error updating level config:', error);
      setMessage('Error updating level configuration');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      setMessage('');

      const response = await fetch('/backend/api/admin/level-settings.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          type: 'reset_defaults'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setMessage('Configuration reset to defaults successfully');
        fetchConfig(); // Reload the data
      } else {
        setMessage('Error resetting configuration');
      }
    } catch (error) {
      console.error('Error resetting config:', error);
      setMessage('Error resetting configuration');
    } finally {
      setSaving(false);
    }
  };

  const handlePointsChange = (id, field, value) => {
    setPointsConfig(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, [field]: field === 'points_value' ? parseInt(value) || 0 : value }
          : item
      )
    );
  };

  const handleLevelChange = (levelNumber, field, value) => {
    setLevelConfig(prev => 
      prev.map(item => 
        item.level_number === levelNumber 
          ? { ...item, [field]: field === 'points_required' ? parseInt(value) || 0 : value }
          : item
      )
    );
  };

  const generateExponentialLevels = () => {
    const newLevels = levelConfig.map((level, index) => {
      if (index === 0) return { ...level, points_required: 0 };
      
      const multiplier = Math.pow(2, index - 1);
      const basePoints = 100;
      const points = basePoints * multiplier;
      
      return { ...level, points_required: points };
    });
    
    setLevelConfig(newLevels);
  };

  const generateLinearLevels = () => {
    const newLevels = levelConfig.map((level, index) => {
      const basePoints = 100;
      const points = basePoints * index;
      
      return { ...level, points_required: points };
    });
    
    setLevelConfig(newLevels);
  };

  if (loading) {
    return (
      <div className="level-settings">
        <div className="loading-spinner">Loading level settings...</div>
      </div>
    );
  }

  return (
    <div className="level-settings">
      <div className="settings-header">
        <h2>Level System Settings</h2>
        <div className="header-actions">
          <button 
            className="reset-button"
            onClick={resetToDefaults}
            disabled={saving}
          >
            Reset to Defaults
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="settings-tabs">
        <button 
          className={`tab ${activeSection === 'points' ? 'active' : ''}`}
          onClick={() => setActiveSection('points')}
        >
          Points Configuration
        </button>
        <button 
          className={`tab ${activeSection === 'levels' ? 'active' : ''}`}
          onClick={() => setActiveSection('levels')}
        >
          Level Thresholds
        </button>
      </div>

      <div className="settings-content">
        {activeSection === 'points' && (
          <div className="points-section">
            <div className="section-header">
              <h3>Activity Points</h3>
              <p>Configure how many points each activity awards</p>
            </div>

            <div className="points-table">
              <div className="table-header">
                <div className="col-activity">Activity</div>
                <div className="col-points">Points</div>
                <div className="col-active">Active</div>
                <div className="col-actions">Actions</div>
              </div>

              {pointsConfig.map((item) => (
                <div key={item.id} className="table-row">
                  <div className="col-activity">
                    <div className="activity-name">{item.activity_name}</div>
                    <div className="activity-type">{item.activity_type}</div>
                    {item.description && (
                      <div className="activity-description">{item.description}</div>
                    )}
                  </div>
                  <div className="col-points">
                    <input
                      type="number"
                      value={item.points_value}
                      onChange={(e) => handlePointsChange(item.id, 'points_value', e.target.value)}
                      min="0"
                      className="points-input"
                    />
                  </div>
                  <div className="col-active">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={item.is_active}
                        onChange={(e) => handlePointsChange(item.id, 'is_active', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div className="col-actions">
                    <button 
                      className="save-button"
                      onClick={updatePointsConfig}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'levels' && (
          <div className="levels-section">
            <div className="section-header">
              <h3>Level Thresholds</h3>
              <p>Configure points required for each level</p>
              <div className="level-actions">
                <button 
                  className="generate-button"
                  onClick={generateExponentialLevels}
                  disabled={saving}
                >
                  Generate Exponential
                </button>
                <button 
                  className="generate-button"
                  onClick={generateLinearLevels}
                  disabled={saving}
                >
                  Generate Linear
                </button>
                <button 
                  className="save-button"
                  onClick={updateLevelConfig}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save All'}
                </button>
              </div>
            </div>

            <div className="levels-table">
              <div className="table-header">
                <div className="col-level">Level</div>
                <div className="col-name">Name</div>
                <div className="col-points">Points Required</div>
                <div className="col-difference">Difference</div>
              </div>

              {levelConfig.map((level, index) => {
                const prevLevel = index > 0 ? levelConfig[index - 1] : null;
                const difference = prevLevel ? level.points_required - prevLevel.points_required : level.points_required;
                
                return (
                  <div key={level.level_number} className="table-row">
                    <div className="col-level">
                      <div className="level-number">{level.level_number}</div>
                    </div>
                    <div className="col-name">
                      <input
                        type="text"
                        value={level.level_name || ''}
                        onChange={(e) => handleLevelChange(level.level_number, 'level_name', e.target.value)}
                        className="name-input"
                        placeholder="Level name"
                      />
                    </div>
                    <div className="col-points">
                      <input
                        type="number"
                        value={level.points_required}
                        onChange={(e) => handleLevelChange(level.level_number, 'points_required', e.target.value)}
                        min="0"
                        className="points-input"
                      />
                    </div>
                    <div className="col-difference">
                      <span className="difference-value">
                        {difference > 0 ? `+${difference.toLocaleString()}` : '0'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LevelSettings;
